import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { SidebarRoot } from './Sidebar.styles';
import './Sidebar.css';
import { navConfig } from './sidebar.config';
import SidebarNavItem from './SidebarNavItem';
import SidebarLogo from './SidebarLogo';
import SidebarNewLead from './SidebarNewLead';
import SidebarUserSearch from './SidebarUserSearch';
import { useSidebarState } from './useSidebarState';
import { useStatusCounts } from './useStatusCounts';
import { MOBILE_MAX_WIDTH, TABLET_MAX_WIDTH } from './breakpoints';
import { SIDEBAR_TRANSITION_MS } from './transition';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_MAX_WIDTH);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_MAX_WIDTH);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}
// click should collapse it back out of the way automatically.
function useIsTabletRange() {
  const inRange = () => window.innerWidth > MOBILE_MAX_WIDTH && window.innerWidth <= TABLET_MAX_WIDTH;
  const [isTabletRange, setIsTabletRange] = useState(inRange);
  useEffect(() => {
    const onResize = () => setIsTabletRange(inRange());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isTabletRange;
}

export default function Sidebar({ isOpenSidebar = true, handler = () => {} }) {
  const decode = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();
  const productType = useSelector((state) => state.ProductTypeFilter.selectedProductType);
  const isPersonalLoanSelected = productType === '2';
  const isMobile = useIsMobile();
  const isTabletRange = useIsTabletRange();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  const sidebarState = useSidebarState({ isOpenSidebar, handler });
  const mode = isMobile || sidebarState.expanded ? 'expanded' : 'collapsed';
  const [isTransitioning, setIsTransitioning] = useState(false);
  const sidebarRef = useRef(null);

  // On tablet, expanding overlays the page instead of resizing it (see Sidebar.css's
  // --sidebar-content-margin-expanded override) — so unlike desktop, a click anywhere
  // on the now-covered main content is expected to dismiss it, the same way a mobile
  // drawer closes on backdrop click. Capture phase so it fires before the click reaches
  // whatever's underneath.
  useEffect(() => {
    if (!isTabletRange || !sidebarState.expanded) return undefined;
    const handleOutsideClick = (event) => {
      if (sidebarRef.current?.contains(event.target)) return;
      sidebarState.collapse();
    };
    document.addEventListener('click', handleOutsideClick, true);
    return () => document.removeEventListener('click', handleOutsideClick, true);
    // sidebarState is a memoized object that also changes on unrelated updates (e.g. any
    // submenu open/close) — depending on the two properties actually used here keeps this
    // listener from re-subscribing on every one of those instead of just expanded/collapse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabletRange, sidebarState.expanded, sidebarState.collapse]);
 
  useLayoutEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => setIsTransitioning(false), SIDEBAR_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [mode]);

  const statusCounts = useStatusCounts({
    isOpen: sidebarState.isOpenAt('status'),
    pathname: location.pathname,
    productType,
  });

  const ctx = {
    role: decode?.role,
    instituteId: decode?.instituteId,
    clientId: decode?.clientId,
    channelPartnerId: decode?.channelPartnerId,
    isPersonalLoanSelected,
  };

  const closeMobileMenu = () => setMobileOpen(false);

  const onNavigate = (path) => {
    sessionStorage.removeItem('page');
    closeMobileMenu();
    if (isTabletRange && sidebarState.expanded) {
      sidebarState.collapse();
    }
    navigate(path);
  };

  const navList = (
    <div className="sidebar-scroll">
      <SidebarUserSearch ctx={ctx} mode={mode} onNavigate={closeMobileMenu} />
      <div className="sidebar-divider" />

      <ul role="menu" data-depth="0" className="sidebar-root-menu">
        {navConfig.map((item) => (
          <SidebarNavItem
            key={item.key}
            item={item}
            depth={0}
            ctx={ctx}
            mode={mode}
            state={sidebarState}
            statusCounts={statusCounts}
            onNavigate={onNavigate}
            ancestorKeys={[]}
          />
        ))}
      </ul>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          className="sidebar-mobile-trigger"
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
        >
          <MenuIcon fontSize="small" />
        </button>
        <div
          className={`sidebar-mobile-backdrop${mobileOpen ? ' sidebar-mobile-backdrop--visible' : ''}`}
          aria-hidden={!mobileOpen}
          onClick={() => setMobileOpen(false)}
        />
        <SidebarRoot
          className={`sidebar-mobile-drawer${mobileOpen ? ' sidebar-mobile-drawer--open' : ''}`}
          aria-label="Main navigation"
          aria-hidden={!mobileOpen}
        >
          <div className="sidebar-toggle-row">
            <button
              type="button"
              className="sidebar-toggle"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>

          <SidebarLogo ctx={ctx} mode="expanded" onNavigate={closeMobileMenu} />
          <SidebarNewLead ctx={ctx} mode="expanded" onNavigate={closeMobileMenu} />
          {navList}
        </SidebarRoot>
      </>
    );
  }

  return (
    <SidebarRoot
      ref={sidebarRef}
      className={`sidebar--${mode}${isTransitioning ? ' sidebar--transitioning' : ''}`}
      aria-label="Main navigation"
    >
      <div className="sidebar-toggle-row">
        <button
          type="button"
          className="sidebar-toggle"
          aria-label={sidebarState.expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          onClick={sidebarState.toggleExpanded}
        >
          <ChevronRightIcon fontSize="small" />
        </button>
      </div>

      <SidebarLogo ctx={ctx} mode={mode} />
      <SidebarNewLead ctx={ctx} mode={mode} />
      {navList}
    </SidebarRoot>
  );
}
